import React, { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { VideoPlayer } from './VideoPlayer';
import { nodeTone } from './nodes/nodeTheme';
import { NodeType } from '../types';
import { X, Twitter, Loader2, Check, ExternalLink, AlertCircle, ImageIcon, Video, Link2 } from 'lucide-react';
import { 
  isTwitterConnected, 
  initiateTwitterAuth, 
  postToTwitter, 
  disconnectTwitter,
  getTwitterUsername,
  getTwitterCharLimit,
  getTwitterUserTier,
  prepareMediaForTwitter,
  TwitterPostResult 
} from '../utils/twitter';

interface ShareToTwitterModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string; // URL to the image/video (blob, data URL, or https URL)
  mediaType: 'image' | 'video';
  defaultCaption?: string;
}

type ModalStep = 'connect' | 'compose' | 'posting' | 'success' | 'error';

export const ShareToTwitterModal: React.FC<ShareToTwitterModalProps> = ({
  isOpen,
  onClose,
  mediaUrl,
  mediaType,
  defaultCaption = '',
}) => {
  const [step, setStep] = useState<ModalStep>('connect');
  const [caption, setCaption] = useState(defaultCaption);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TwitterPostResult | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [userTier, setUserTier] = useState<'basic' | 'premium'>('basic');
  const titleId = useId();

  // Dynamic character limit based on user tier
  const charLimit = getTwitterCharLimit();
  const charsRemaining = charLimit - caption.length;

  // Initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCaption(defaultCaption);
      setError(null);
      setResult(null);
      
      // Check if already connected
      if (isTwitterConnected()) {
        setUsername(getTwitterUsername());
        setUserTier(getTwitterUserTier());
        setStep('compose');
      } else {
        setStep('connect');
      }
    }
  }, [isOpen, defaultCaption]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    
    try {
      const tokens = await initiateTwitterAuth();
      setUsername(tokens.username || null);
      setUserTier(tokens.userTier || 'basic');
      setStep('compose');
    } catch (err: any) {
      setError(err.message || 'Failed to connect to Twitter');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    disconnectTwitter();
    setUsername(null);
    setStep('connect');
  };

  const handlePost = async (textOnly: boolean = false) => {
    setIsPosting(true);
    setError(null);
    setStep('posting');

    try {
      // Twitter Free tier with OAuth 2.0 only supports text tweets
      // Media upload requires OAuth 1.0a which needs a paid plan
      if (textOnly) {
        // Post text-only tweet
        if (!caption?.trim()) {
          throw new Error('Please enter a caption for your text-only tweet');
        }
        
        const postResult = await postToTwitter({
          text: caption,
        });

        setResult(postResult);

        if (postResult.success) {
          setStep('success');
        } else {
          setError(postResult.error || 'Failed to post tweet');
          setStep('error');
        }
      } else {
        // Try with media - will fail on Free tier
        const media = await prepareMediaForTwitter(mediaUrl);
        
        if (!media) {
          throw new Error('Failed to prepare media for upload');
        }

        let mimeType = media.mimeType;
        if (mediaType === 'video' && !mimeType.startsWith('video/')) {
          mimeType = 'video/mp4';
        } else if (mediaType === 'image' && !mimeType.startsWith('image/')) {
          mimeType = 'image/png';
        }

        const postResult = await postToTwitter({
          text: caption || undefined,
          mediaBase64: media.base64,
          mediaType: mimeType,
        });

        setResult(postResult);

        if (postResult.success) {
          setStep('success');
        } else {
          // Check if it's the media upload limitation
          if (postResult.error?.includes('OAuth 1.0a') || postResult.error?.includes('media upload')) {
            setError('Twitter Free tier does not support image uploads with OAuth 2.0. You can post a text-only tweet instead.');
          } else {
            setError(postResult.error || 'Failed to post tweet');
          }
          setStep('error');
        }
      }
    } catch (err: any) {
      if (err.message?.includes('OAuth 1.0a') || err.message?.includes('media upload')) {
        setError('Twitter Free tier does not support image uploads. You can post a text-only tweet instead.');
      } else {
        setError(err.message || 'Failed to post to Twitter');
      }
      setStep('error');
    } finally {
      setIsPosting(false);
    }
  };

  const handleViewTweet = () => {
    if (result?.tweetUrl) {
      window.open(result.tweetUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  // An upload that failed can still go out as text, when there is a caption to send
  const canPostTextOnly = !!(error?.includes('media') || error?.includes('OAuth 1.0a') || error?.includes('image upload')) && !!caption?.trim();

  const modal = (
    <div className="ns-modal-scrim">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="ns-modal-sheet ns-modal-sheet-flush">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pl-6 pr-5 pt-5 pb-4 border-b border-cream-300">
          <div className="flex items-center gap-3 min-w-0">
            {/* The mark of X stays in its blue, from the palette's sky tone */}
            <span data-tone="sky" className="ns-mark">
              <Twitter />
            </span>
            <div className="min-w-0">
              <h2 id={titleId} className="ns-t-dialog">
                {step === 'connect' && 'Connect Twitter'}
                {step === 'compose' && 'Share to Twitter'}
                {step === 'posting' && 'Posting...'}
                {step === 'success' && 'Posted!'}
                {step === 'error' && 'Post failed'}
              </h2>
              {username && step === 'compose' && (
                <p className="text-sm text-ink-soft truncate">@{username}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="ns-icon-btn" aria-label="Close">
            <X />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Connect Step */}
          {step === 'connect' && (
            <>
              <div className="ns-empty">
                <span data-tone="sky" className="ns-mark ns-mark-lg">
                  <Twitter />
                </span>
                <h3 className="ns-empty-title">Connect your account</h3>
                <p className="ns-empty-text">
                  Connect your Twitter account to share your creations directly.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="ns-btn ns-btn-sm ns-btn-primary mt-3"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Twitter size={14} />
                      Connect Twitter
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className="ns-note" data-tone="danger">
                  <AlertCircle size={14} />
                  <p>{error}</p>
                </div>
              )}
            </>
          )}

          {/* Compose Step */}
          {step === 'compose' && (
            <>
              {/* Media Preview: a picture lies on cream, a clip plays on ink */}
              <div className="mb-4 rounded-xl overflow-hidden bg-cream-50 border border-cream-300">
                {mediaType === 'image' ? (
                  <img
                    src={mediaUrl}
                    alt="Preview"
                    className="w-full h-48 object-contain"
                  />
                ) : (
                  <VideoPlayer src={mediaUrl} className="w-full h-48" muted />
                )}
                <div className="px-3 py-2 flex items-center border-t border-cream-300">
                  {/* The kind of media, in the colour of the node it comes from */}
                  <span
                    data-tone={nodeTone(mediaType === 'image' ? NodeType.IMAGE_GENERATOR : NodeType.VIDEO_GENERATOR)}
                    className="ns-chip ns-chip-sm ns-chip-tone"
                  >
                    {mediaType === 'image' ? (
                      <ImageIcon size={12} />
                    ) : (
                      <Video size={12} />
                    )}
                    <span className="capitalize">{mediaType}</span>
                  </span>
                </div>
              </div>

              {/* Caption Input */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="ns-field-label mb-0">Caption</label>
                  <span className={`text-xs ${charsRemaining < 0 ? 'text-danger-deep font-semibold' : charsRemaining < 20 ? 'text-warn-deep' : 'text-ink-faint'}`}>
                    {charsRemaining}
                  </span>
                </div>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="What's happening? (optional)"
                  className="ns-field h-24 resize-none"
                  maxLength={charLimit + 100} // Allow typing over but show warning
                />
              </div>

              {/* Connected Account */}
              <div className="ns-well flex items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span data-tone="sky" className="ns-mark ns-mark-sm">
                    <Twitter />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink flex items-center gap-2">
                      <span className="truncate">@{username || 'Connected'}</span>
                      {userTier === 'premium' && (
                        <span className="ns-chip ns-chip-sm ns-chip-lavender">
                          Premium
                        </span>
                      )}
                    </p>
                    <p className="ns-t-small">{charLimit.toLocaleString()} char limit</p>
                  </div>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="ns-btn ns-btn-xs ns-btn-ghost ns-btn-flat"
                >
                  Disconnect
                </button>
              </div>

              {error && (
                <div className="ns-note mt-4" data-tone="danger">
                  <AlertCircle size={14} />
                  <p>{error}</p>
                </div>
              )}

              {/* The dialog's action, at the bottom right as in every dialog */}
              <div className="flex flex-wrap justify-end gap-3 mt-6">
                <button
                  onClick={() => handlePost(false)}
                  disabled={charsRemaining < 0}
                  className="ns-btn ns-btn-sm ns-btn-primary"
                >
                  <Twitter size={14} />
                  Post to Twitter
                </button>
              </div>
            </>
          )}

          {/* Posting Step */}
          {step === 'posting' && (
            <div className="ns-empty">
              <span data-tone="sky" className="ns-mark ns-mark-lg">
                <Loader2 className="animate-spin" />
              </span>
              <h3 className="ns-empty-title">Posting to Twitter...</h3>
              <p className="ns-empty-text">
                {mediaType === 'video' ? 'Uploading video (this may take a moment)...' : 'Uploading your image...'}
              </p>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <>
              <div className="ns-empty">
                <span data-tone="ok" className="ns-mark ns-mark-lg">
                  <Check />
                </span>
                <h3 className="ns-empty-title">Posted successfully!</h3>
                <p className="ns-empty-text">
                  Your {mediaType} has been shared to Twitter.
                </p>
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                {/* Close is the one action of the step when there is no tweet to open */}
                <button
                  onClick={onClose}
                  className={`ns-btn ns-btn-sm ${result?.tweetUrl ? 'ns-btn-soft' : 'ns-btn-primary'}`}
                >
                  Close
                </button>
                {result?.tweetUrl && (
                  <button
                    onClick={handleViewTweet}
                    className="ns-btn ns-btn-sm ns-btn-primary"
                  >
                    <ExternalLink size={14} />
                    View tweet
                  </button>
                )}
              </div>
            </>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <>
              <div className="ns-empty">
                <span data-tone="danger" className="ns-mark ns-mark-lg">
                  <AlertCircle />
                </span>
                <h3 className="ns-empty-title">Post failed</h3>
                <p className="ns-empty-text text-danger-deep break-words">{error}</p>
              </div>

              {/* Show text-only option if it's a media upload error */}
              {canPostTextOnly && (
                <div className="ns-note mb-6 justify-center" data-tone="lavender">
                  <p>You can still post your caption as a text-only tweet!</p>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={onClose}
                  className="ns-btn ns-btn-sm ns-btn-soft"
                >
                  Cancel
                </button>
                {/* Going back to the caption is the way on, unless the text can be posted as it is */}
                <button
                  onClick={() => setStep('compose')}
                  className={`ns-btn ns-btn-sm ${canPostTextOnly ? 'ns-btn-ghost' : 'ns-btn-primary'}`}
                >
                  Edit caption
                </button>
                {canPostTextOnly && (
                  <button
                    onClick={() => handlePost(true)}
                    className="ns-btn ns-btn-sm ns-btn-primary"
                  >
                    <Twitter size={14} />
                    Post text only
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modal, document.body);
};

export default ShareToTwitterModal;
